import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AdminNavigationStateService {

  private adminNavigationStateSubject = new BehaviorSubject<boolean>(false); // auto close
  public adminNavigationState$: Observable<boolean> = this.adminNavigationStateSubject.asObservable();

  readonly #router = inject(Router);

  constructor() {

    // Routen-gesteuertes Auto-Close: Routen, die die Admin-Navigation (dx-drawer)
    // nicht neben sich haben wollen, deklarieren das per `data: { closeAdminNav: true }`
    // in ihrer Routen-Definition (siehe pages.module.ts). Zentral hier statt an jedem
    // Button, damit es auch für Browser-Back/Forward, Deep-Links und programmatische
    // Navigationen gilt.
    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        // Nur schließen, wenn wirklich offen -> keine überflüssigen Emits an die Consumer.
        if (this.#shouldCloseOnCurrentRoute() && this.getState()) {
          this.setState(false);
        }
        // Auto-open: admin pages (e.g. /admin/statistics/dashboard) declare `data: { openAdminNav: true }`
        // so the admin navigation is open on a deep link / reload as well. NavigationEnd
        // only fires after all guards have passed, so a route rejected by accessGuard never
        // opens it. Not on the phone: the side navigation is not rendered there
        // (see page-content.component.html).
        else if (this.#shouldOpenOnCurrentRoute() && !this.getState() && !this.#isPhone()) {
          this.setState(true);
        }
      });

  }

  setState(isOpen: boolean): void {
    this.adminNavigationStateSubject.next(isOpen);
  }

  toggleState(): void {
    this.setState(!this.adminNavigationStateSubject.value);
  }

  getState(): boolean {
    return this.adminNavigationStateSubject.value;
  }

  // Prüft die KOMPLETTE aktive Routen-Kette (Root -> Blatt) auf `closeAdminNav`.
  // Bewusst nicht nur das Blatt: so kann das Flag auch auf einer Eltern-Route für
  // einen ganzen Teilbaum gesetzt werden (z. B. 'profile' mit loadChildren),
  // unabhängig von der paramsInheritanceStrategy.
  #shouldCloseOnCurrentRoute(): boolean {
    return this.#hasRouteFlag('closeAdminNav');
  }

  /** Same chain lookup as #shouldCloseOnCurrentRoute(), for `openAdminNav`. */
  #shouldOpenOnCurrentRoute(): boolean {
    return this.#hasRouteFlag('openAdminNav');
  }

  #hasRouteFlag(flag: 'closeAdminNav' | 'openAdminNav'): boolean {
    let route: ActivatedRoute | null = this.#router.routerState.root;

    while (route) {
      if (route.snapshot.data[flag] === true) return true;
      route = route.firstChild;
    }

    return false;
  }

  /** 768px = 'phone' breakpoint, same as PageContentComponent.isPhone$. */
  #isPhone(): boolean {
    return window.innerWidth <= 768;
  }

}
